/**
 * Renderer dei blocchi.
 *
 * L'agente emette blocchi tipizzati; qui ognuno diventa un componente della
 * libreria. Il testo è solo il collante fra un blocco e l'altro.
 */

import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Info,
  XCircle,
} from "lucide-react";
import { formatEur, formatRange, type ChatBlock } from "@otto/shared";
import { Card, Pill, Thumb, cx } from "../ui/primitives.tsx";
import { DocumentoCosti } from "./DocumentoCosti.tsx";
import { ProposalCard } from "./ProposalCard.tsx";
import { Receipt } from "./Receipt.tsx";
import { useStore } from "../lib/store.tsx";

export function BlockRenderer({
  block,
  onSend,
}: {
  block: ChatBlock;
  onSend: (text: string) => void;
}) {
  switch (block.type) {
    case "proposal_card":
      return (
        <ProposalCard actionId={block.actionId} fallback={block.preview} />
      );
    case "disambiguation":
      return <Disambiguation block={block} onSend={onSend} />;
    case "chips":
      return <Chips block={block} onSend={onSend} />;
    case "receipt":
      return <ReceiptBlock block={block} />;
    case "calendar_preview":
      return <CalendarPreview block={block} />;
    case "notice":
      return <Notice block={block} />;
    case "listings":
      return <Listings block={block} onSend={onSend} />;
    case "documento_costi":
      return <DocumentoCosti block={block} />;
    case "allegato":
      // Sta sul messaggio dell'host, che lo disegna da sé.
      return null;
  }
}

// ── Disambiguazione: opzioni a tap, mai "riscrivi meglio" ───────────────────

function Disambiguation({
  block,
  onSend,
}: {
  block: Extract<ChatBlock, { type: "disambiguation" }>;
  onSend: (text: string) => void;
}) {
  return (
    <Card size="lg" className="animate-rise p-4">
      <p className="mb-3 text-[15px] font-bold text-ink">{block.question}</p>
      <div className="flex flex-col gap-2">
        {block.options.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onSend(option.value)}
            className={cx(
              "flex items-center gap-3 rounded-card bg-surface-muted/70 p-2.5 text-left",
              "transition-all duration-quick ease-soft hover:bg-accent-soft active:scale-[0.99]",
            )}
          >
            <Thumb src={option.photoUrl} alt={option.label} size={38} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-ink">
                {option.label}
              </span>
              {option.sublabel ? (
                <span className="block truncate text-[12px] text-ink-faint">
                  {option.sublabel}
                </span>
              ) : null}
            </span>
            <ChevronRight size={16} className="shrink-0 text-ink-faint" />
          </button>
        ))}
      </div>
    </Card>
  );
}

// ── Chip: preparano il comando, non lo eseguono ─────────────────────────────

function Chips({
  block,
  onSend,
}: {
  block: Extract<ChatBlock, { type: "chips" }>;
  onSend: (text: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {/* La domanda sta nel blocco e non nel testo di Otto: due bottoni «Sì» e
          «No» senza la domanda sopra sono un indovinello. */}
      {block.question ? (
        <p className="text-[15px] text-ink">{block.question}</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {block.chips.map((chip) => (
          <Pill
            key={chip.id}
            size="sm"
            variant={chip.tone === "accento" ? "primary" : "secondary"}
            onClick={() => onSend(chip.prompt)}
          >
            {chip.label}
          </Pill>
        ))}
      </div>
    </div>
  );
}

// ── Ricevuta come blocco a sé ───────────────────────────────────────────────

function ReceiptBlock({
  block,
}: {
  block: Extract<ChatBlock, { type: "receipt" }>;
}) {
  const store = useStore();
  const receipt = store.getAction(block.actionId)?.receipt ?? block.receipt;
  if (!receipt) return null;

  return (
    <Card className="animate-rise p-4">
      <p className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-ink-faint">
        Ricevuta di sincronizzazione
      </p>
      <Receipt receipt={receipt} />
    </Card>
  );
}

// ── Anteprima calendario ────────────────────────────────────────────────────

function CalendarPreview({
  block,
}: {
  block: Extract<ChatBlock, { type: "calendar_preview" }>;
}) {
  return (
    <Card className="animate-rise p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <p className="truncate text-sm font-bold text-ink">{block.nickname}</p>
        <p className="shrink-0 text-[12px] text-ink-faint">
          {formatRange(block.from, block.to)}
        </p>
      </div>

      <div className="scroll-soft -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
        {block.days.map((day) => (
          <div
            key={day.date}
            className={cx(
              "flex w-14 shrink-0 flex-col items-center gap-0.5 rounded-2xl px-1 py-2",
              day.booked
                ? "bg-surface-muted text-ink-faint"
                : "bg-accent-soft text-ink",
            )}
          >
            <span className="text-[10px] font-semibold uppercase">
              {new Intl.DateTimeFormat("it-IT", {
                weekday: "short",
                timeZone: "UTC",
              })
                .format(new Date(`${day.date}T00:00:00Z`))
                .replace(".", "")}
            </span>
            <span className="text-[13px] font-bold tabular-nums">
              {Number(day.date.slice(8, 10))}
            </span>
            <span className="text-[11px] tabular-nums">
              {day.booked
                ? "—"
                : day.priceCents != null
                  ? formatEur(day.priceCents)
                  : "·"}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Avvisi ──────────────────────────────────────────────────────────────────

function Notice({ block }: { block: Extract<ChatBlock, { type: "notice" }> }) {
  const config = {
    info: { Icon: Info, wrap: "bg-surface-muted", text: "text-ink-soft" },
    attenzione: {
      Icon: AlertTriangle,
      wrap: "bg-warn-soft",
      text: "text-warn",
    },
    errore: { Icon: XCircle, wrap: "bg-danger-soft", text: "text-danger" },
    successo: { Icon: CheckCircle2, wrap: "bg-ok-soft", text: "text-ok" },
  }[block.tone];

  return (
    <div
      className={cx(
        "animate-rise flex gap-2.5 rounded-card px-3.5 py-3",
        config.wrap,
      )}
    >
      <config.Icon size={16} className={cx("mt-0.5 shrink-0", config.text)} />
      <div className="min-w-0">
        {block.title ? (
          <p className={cx("text-sm font-bold", config.text)}>{block.title}</p>
        ) : null}
        <p className={cx("text-[13px] leading-relaxed", config.text)}>
          {block.message}
        </p>
      </div>
    </div>
  );
}

// ── Elenco immobili ─────────────────────────────────────────────────────────

function Listings({
  block,
  onSend,
}: {
  block: Extract<ChatBlock, { type: "listings" }>;
  onSend: (text: string) => void;
}) {
  return (
    <div className="animate-rise flex flex-col gap-2">
      {block.listings.map((listing) => (
        <Card
          key={listing.id}
          interactive
          className="flex items-center gap-3 p-2.5"
          onClick={() => onSend(`Parlami di "${listing.nickname}"`)}
        >
          <Thumb src={listing.photoUrl} alt={listing.nickname} size={40} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink">
              {listing.nickname}
            </p>
            {listing.city ? (
              <p className="text-[12px] text-ink-faint">{listing.city}</p>
            ) : null}
          </div>
          {listing.stat ? (
            <span className="shrink-0 text-[13px] font-bold tabular-nums text-ink-soft">
              {listing.stat}
            </span>
          ) : null}
        </Card>
      ))}
    </div>
  );
}
